import { ArrowRight, Clock } from "lucide-react";
import { pageAt } from "../../content";
import { cx } from "../../lib/cx";
import { parseTeamTitle } from "../../lib/content-blocks";
import { imageProps, localHref, pathnameOf, photoProps } from "../../lib/links";

const cardImage = (card) => card.image || pageAt(pathnameOf(card.href))?.image;

/** Words in a captured page's body, for a reading-time estimate. */
function wordCount(page) {
  return (page?.body || [])
    .map((b) => b.text || b.html?.replace(/<[^>]+>/g, " ") || "")
    .join(" ")
    .split(/\s+/)
    .filter(Boolean).length;
}

const months =
  "January February March April May June July August September October November December".split(
    " ",
  );

/** "October 5, 2026" from "2026-10-05" (no Date, so no time-zone drift). */
export function postDate(iso) {
  if (!iso) return undefined;
  const [year, month, day] = iso.split("-").map(Number);
  return `${months[month - 1]} ${day}, ${year}`;
}

/**
 * "4 min read" for a captured page (path or page object); undefined when the
 * page is too thin to call it reading (e.g. a single embedded video).
 */
export function readingTime(pageOrPath) {
  const page =
    typeof pageOrPath === "string"
      ? pageAt(pathnameOf(pageOrPath))
      : pageOrPath;
  const words = wordCount(page);
  return words < 120
    ? undefined
    : `${Math.max(1, Math.round(words / 230))} min read`;
}

/** Photo-filled card with the title on a dark gradient (case studies). */
export function StoryCard({
  card,
  image,
  label = "Read More",
  className = "h-112.5",
}) {
  return (
    <a
      href={localHref(card.href)}
      className={cx(
        "group relative block overflow-hidden rounded-brand bg-ink shadow-soft",
        className,
      )}
    >
      <img
        {...(image
          ? photoProps(image, "(max-width: 768px) 100vw, 420px")
          : imageProps(
              localHref(cardImage(card)),
              "(max-width: 768px) 100vw, 420px",
            ))}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 flex flex-col justify-end bg-linear-to-t from-ink/95 via-ink/45 to-transparent p-7">
        <h3 className="font-display mb-4 text-2xl leading-[1.15] text-white">
          {card.title}
        </h3>
        <span className="flex items-center gap-2 font-bold text-yellow">
          {label}
          <ArrowRight
            size={17}
            className="transition-transform group-hover:translate-x-1"
          />
        </span>
      </div>
    </a>
  );
}

/**
 * Photo-on-top card with a white body (blog posts). `featured` lays it out
 * side by side across the whole grid row; `eyebrow` labels it.
 */
export function ArticleCard({
  card,
  featured = false,
  label = "Read the post",
  eyebrow,
  headingLevel: Heading = "h3",
}) {
  const time = readingTime(card.href);
  return (
    <a
      href={localHref(card.href)}
      className={cx(
        "group flex flex-col overflow-hidden rounded-brand border border-line bg-white shadow-soft transition duration-200 hover:-translate-y-0.5 hover:border-blue/40 hover:shadow-lift motion-reduce:hover:translate-y-0",
        featured && "md:col-span-2 lg:col-span-3 lg:flex-row",
      )}
    >
      <div
        className={cx(
          "overflow-hidden bg-surface",
          featured
            ? "aspect-video lg:aspect-auto lg:w-[58%] lg:shrink-0"
            : "aspect-16/10",
        )}
      >
        <img
          {...imageProps(
            localHref(cardImage(card)),
            featured
              ? "(max-width: 1024px) 100vw, 650px"
              : "(max-width: 768px) 100vw, 360px",
          )}
          alt=""
          loading={featured ? undefined : "lazy"}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div
        className={cx(
          "flex flex-1 flex-col p-6 md:p-7",
          featured && "lg:justify-center lg:p-12",
        )}
      >
        {(eyebrow || time || card.date) && (
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            {eyebrow && (
              <span className="eyebrow text-blue-deep">{eyebrow}</span>
            )}
            {card.date && (
              <time dateTime={card.date} className="text-sm text-muted">
                {postDate(card.date)}
              </time>
            )}
            {time && (
              <span className="flex items-center gap-1.5 text-sm text-muted">
                <Clock size={14} aria-hidden="true" />
                {time}
              </span>
            )}
          </div>
        )}
        <Heading
          className={cx(
            "font-display text-slate transition-colors group-hover:text-ink",
            featured
              ? "text-[28px] leading-[1.1] md:text-[38px]"
              : "text-[22px] leading-[1.18]",
          )}
        >
          {card.title}
        </Heading>
        <span className="mt-auto flex items-center gap-2 pt-6 font-bold text-blue-deep">
          {label}
          <ArrowRight
            size={17}
            className="transition-transform group-hover:translate-x-1"
          />
        </span>
      </div>
    </a>
  );
}

/** Team portrait with name and role over a gradient. */
export function TeamCard({ card }) {
  const { name, role } = parseTeamTitle(card.title);
  return (
    <a
      href={localHref(card.href)}
      className="group relative block h-90 overflow-hidden rounded-brand bg-surface shadow-soft"
    >
      <img
        {...imageProps(
          localHref(card.image),
          "(max-width: 640px) 100vw, 300px",
        )}
        alt={name}
        loading="lazy"
        className="h-full w-full object-cover object-[center_25%] transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 flex flex-col justify-end bg-linear-to-t from-ink/90 via-ink/20 to-transparent p-6">
        <h3 className="font-display text-2xl text-white uppercase">{name}</h3>
        <p className="mb-3 text-sm font-bold text-yellow">{role}</p>
        <span className="flex items-center gap-1 text-sm font-bold text-white/80 transition-colors group-hover:text-white">
          Read More <ArrowRight size={15} />
        </span>
      </div>
    </a>
  );
}
